export const SUNCAST_SNAPSHOT_CAPTURE_SLOT_COUNT = 5;
export const SUNCAST_SNAPSHOT_ROW_COUNT = SUNCAST_SNAPSHOT_CAPTURE_SLOT_COUNT;

export enum SuncastSnapshotStatus {
    Captured = 'captured',
    Complete = 'complete',
    Failed = 'failed',
}

export interface SuncastSnapshotPoint {
    id: string;
    x: number;
    y: number;
}

export interface SuncastSnapshotProductEntry {
    captureSlot: string;
    productId: string;
    productVersionId: string;
    partNumber: string;
    description: string;
    imageUrl: string;
    count: number;
    pointsJson: string;
    counted: boolean;
}

export interface SuncastSnapshot {
    id: string;
    captureDate: string;
    capturedOn: Date;
    createdOn: Date;
    originalUrl: string;
    undistortedUrl: string;
    status: SuncastSnapshotStatus;
    rowCount: number;
    errorMessage: string;
    productEntriesJson: string;

    completedById: string | null;
    completedByName: string | null;
    completedOn: Date | null;
    updatedById: string | null;
    updatedByName: string | null;
    updatedOn: Date | null;
}

export interface SuncastRowProductsConfig {
    productIdsByCaptureSlot?: { [captureSlot: string]: string };
    productIdsByRowIndex?: { [rowIndex: string]: string };
}

export function createEmptySuncastSnapshot(args: {
    id: string;
    captureDate: string;
    capturedOn: Date;
    createdOn?: Date;
    originalUrl?: string;
    undistortedUrl?: string;
    rowCount?: number;
    status?: SuncastSnapshotStatus;
    errorMessage?: string;
    productEntriesJson?: string;
}): SuncastSnapshot {
    return {
        id: args.id,
        captureDate: args.captureDate,
        capturedOn: args.capturedOn,
        createdOn: args.createdOn ?? new Date(),
        originalUrl: args.originalUrl ?? '',
        undistortedUrl: args.undistortedUrl ?? '',
        status: args.status ?? SuncastSnapshotStatus.Captured,
        rowCount: args.rowCount ?? 0,
        errorMessage: args.errorMessage ?? '',
        productEntriesJson: args.productEntriesJson ?? '[]',
        completedById: null,
        completedByName: null,
        completedOn: null,
        updatedById: null,
        updatedByName: null,
        updatedOn: null,
    };
}

export function suncastCaptureSlot(index: number): string {
    return `slice-${String(index).padStart(2, '0')}`;
}

export function getSuncastMappedProductId(
    config: SuncastRowProductsConfig | null | undefined,
    captureSlotIndex: number,
): string {
    const captureSlot = suncastCaptureSlot(captureSlotIndex);
    return config?.productIdsByCaptureSlot?.[captureSlot]
        ?? config?.productIdsByRowIndex?.[String(captureSlotIndex)]
        ?? '';
}

function normalizeSuncastSnapshotProductEntry(entry: Partial<SuncastSnapshotProductEntry>): SuncastSnapshotProductEntry {
    return {
        captureSlot: entry.captureSlot ?? '',
        productId: entry.productId ?? '',
        productVersionId: entry.productVersionId ?? '',
        partNumber: entry.partNumber ?? '',
        description: entry.description ?? '',
        imageUrl: entry.imageUrl ?? '',
        count: entry.count ?? 0,
        pointsJson: entry.pointsJson ?? '[]',
        counted: entry.counted ?? (entry.count ?? 0) > 0,
    };
}

export function parseSuncastSnapshotProductEntriesJson(value: string | null | undefined): SuncastSnapshotProductEntry[] {
    if (!value) return [];
    try {
        const parsed = JSON.parse(value);
        if (!Array.isArray(parsed)) return [];
        return parsed
            .filter(entry => entry && typeof entry === 'object')
            .map(entry => normalizeSuncastSnapshotProductEntry(entry));
    } catch {
        return [];
    }
}

export function stringifySuncastSnapshotProductEntries(entries: SuncastSnapshotProductEntry[]): string {
    return JSON.stringify(entries.map(entry => normalizeSuncastSnapshotProductEntry(entry)));
}

export function getSuncastSnapshotProductEntries(snapshot: SuncastSnapshot): SuncastSnapshotProductEntry[] {
    return parseSuncastSnapshotProductEntriesJson(snapshot.productEntriesJson);
}

export function setSuncastSnapshotProductEntries(
    snapshot: SuncastSnapshot,
    entries: SuncastSnapshotProductEntry[],
): SuncastSnapshot {
    return {
        ...snapshot,
        productEntriesJson: stringifySuncastSnapshotProductEntries(entries),
    };
}
